import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    Heading,
    ProgressiveImage,
} from '@gitbook/spine-react';

import type { UseUpgradeSite } from '../../../state';
import { createDocumentationURL } from '../../../utils';
import { CreateSiteContentButton } from './CreateSiteContentButton';
import {
    CreateSiteSectionButton,
    type CreateSiteSectionButtonProps,
} from './CreateSiteSectionButton';
import {
    CreateSiteVariantButton,
    type CreateSiteVariantButtonProps,
} from './CreateSiteVariantButton';
import sectionsDark from './images/sections-dark.svg';
import sectionsLight from './images/sections-light.svg';
import variantsDark from './images/variants-dark.svg';
import variantsLight from './images/variants-light.svg';

interface SiteStructureEmptyStateProps
    extends Omit<CreateSiteSectionButtonProps, 'label'>,
        Omit<CreateSiteVariantButtonProps, 'label' | 'siteSection'> {
    upgrade: UseUpgradeSite | null;
    /**
     * Indicates if the site is empty (nothing in it) or not.
     */
    isEmpty: boolean;
    siteStructure: api.SiteStructure;
}

export function SiteStructureEmptyState(props: SiteStructureEmptyStateProps) {
    const { isEmpty } = props;
    if (isEmpty) {
        return <ContentCard {...props} />;
    }
    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SectionCard {...props} />
            <VariantCard {...props} />
        </div>
    );
}

function ContentCard(props: SiteStructureEmptyStateProps) {
    const { upgrade, ...rest } = props;
    return (
        <div className="rounded-lg border bg-base">
            <EmptyState className="w-full">
                <ProgressiveImage
                    src={{ light: sectionsLight, dark: sectionsDark }}
                    width={456}
                    height={170}
                    alt="Sections"
                    className="mb-2 h-auto w-full shrink-0 border object-cover"
                />
                <Heading>Start building your site</Heading>
                <Description>
                    Create a space to begin publishing documentation, guides, or changelogs.
                </Description>
                <EmptyStateActions>
                    <CreateSiteContentButton {...rest} label="Add space" />
                </EmptyStateActions>
            </EmptyState>
        </div>
    );
}

function SectionCard(props: SiteStructureEmptyStateProps) {
    const { upgrade, siteStructure, ...rest } = props;
    return (
        <EmptyState className="w-full border bg-base">
            <ProgressiveImage
                src={{ light: sectionsLight, dark: sectionsDark }}
                width={456}
                height={170}
                alt="Sections"
                className="mb-2 block h-auto w-full shrink-0 border object-cover"
            />
            <Heading>Centralize your documentation</Heading>
            <Description>
                Create a seamless experience by breaking your docs into logical sections-like
                products, features, or topics.
            </Description>
            <EmptyStateActions>
                {upgrade ? (
                    <Button {...upgrade} />
                ) : (
                    <CreateSiteSectionButton
                        {...rest}
                        siteStructure={siteStructure}
                        label="Add section"
                        kind="primary"
                    />
                )}
                <Button
                    kind="blank"
                    target="_blank"
                    label="Learn more"
                    leadingIcon={Icon.ExternalLink}
                    href={createDocumentationURL(
                        'published-documentation/site-structure-and-navigation/site-sections',
                        'docs'
                    )}
                />
            </EmptyStateActions>
        </EmptyState>
    );
}

function VariantCard(props: SiteStructureEmptyStateProps) {
    return (
        <EmptyState className="w-full border bg-base">
            <ProgressiveImage
                src={{ light: variantsLight, dark: variantsDark }}
                width={456}
                height={170}
                alt="Variants"
                className="mb-2 h-auto w-full shrink-0 border object-cover"
            />
            <Heading>Support versions & languages</Heading>
            <Description>
                Variants let you show different versions of the same content-like product releases
                or translated languages.
            </Description>
            <EmptyStateActions>
                <CreateSiteVariantButton
                    {...props}
                    siteSection={null}
                    label="Add variant"
                    kind="primary"
                />
                <Button
                    kind="blank"
                    target="_blank"
                    label="Learn more"
                    leadingIcon={Icon.ExternalLink}
                    href={createDocumentationURL(
                        'publishing-documentation/site-structure/variants'
                    )}
                />
            </EmptyStateActions>
        </EmptyState>
    );
}
